import { requiredStreetAddress } from '@trashlab/api/src/domains/core/validation/address-schema';
import type { Address } from '@trashlab/api/src/domains/core/types';
import { getMissingJobSiteAddressFields } from '@trashlab/api/src/domains/customer/shared/job-site-address';

export const CHECKOUT_SITE_ADDRESS_ERROR =
  'Please enter a full service address, including state, before completing checkout.';

export class CheckoutSiteAddressError extends Error {
  constructor() {
    super(CHECKOUT_SITE_ADDRESS_ERROR);
    this.name = 'CheckoutSiteAddressError';
    Object.setPrototypeOf(this, CheckoutSiteAddressError.prototype);
  }
}

// Persisted JSON can contain numeric coordinates as strings. Normalize only
// decimal strings; leave invalid values intact so address validation rejects them.
export function normalizeCheckoutSiteAddress(siteAddress: unknown): unknown {
  if (
    !siteAddress ||
    typeof siteAddress !== 'object' ||
    Array.isArray(siteAddress)
  ) {
    return siteAddress;
  }
  const address = { ...siteAddress } as Record<string, unknown>;
  for (const key of ['latitude', 'longitude']) {
    const value = address[key];
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmed)) continue;
    const coordinate = Number(trimmed);
    if (Number.isFinite(coordinate)) address[key] = coordinate;
  }
  return address;
}

export function isValidCheckoutSiteAddress(siteAddress: unknown): boolean {
  const parsed = requiredStreetAddress.safeParse(
    normalizeCheckoutSiteAddress(siteAddress),
  );

  // Checkout creates a job site, which needs a state.
  return Boolean(
    parsed.success &&
      parsed.data.streetAddressPartOne.trim() &&
      getMissingJobSiteAddressFields(parsed.data).length === 0,
  );
}

export function assertValidCheckoutSiteAddress(
  siteAddress: unknown,
): asserts siteAddress is Address {
  const parsed = requiredStreetAddress.safeParse(siteAddress);
  if (
    !parsed.success ||
    !parsed.data.streetAddressPartOne.trim() ||
    getMissingJobSiteAddressFields(parsed.data).length > 0
  ) {
    throw new CheckoutSiteAddressError();
  }
}
